import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams, HttpErrorResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Constants} from '../../constants';
import {ConversationId} from '../conversation-id/conversation-id';

@Injectable({
    providedIn: 'root'
})
export class RequestExecutor {
    /**
     * Request URL
     * @var {string}
     */
    private _requestUrl: string;

    /**
     * Custom request headers
     * @var {HttpHeaders}
     */
    private _requestHeaders: HttpHeaders;

    /**
     * Request body
     * @var {object}
     */
    private _requestBody: object;

    /**
     * Request parameters
     * @var {HttpParams}
     */
    private _requestParams: HttpParams;

    /**
     * Default headers to add to all requests
     * @var {object}
     */
    private readonly _defaultRequestHeaders: object = Constants.DEFAULT_REQUEST_HEADERS;

    /**
     * Class constructor
     *
     * @param _conversationId
     * @param _httpClient
     */
    constructor(
        protected _conversationId: ConversationId,
        protected _httpClient: HttpClient
    ) {
        this._defaultRequestHeaders = {
            ...this._defaultRequestHeaders,
            ...{
                'x-conversation-id': this._conversationId.getConversationId()
            }
        };
    }

    /**
     * Executes an HTTP Request
     *
     * @param type
     * @param url
     * @returns {Observable<object>}
     */
    public executeRequest(type: ('get'|'post'), url: string): Observable<object> {
        this._requestUrl = url.trim();

        // @ts-ignore
        return this._httpClient[type](...this._generateFunctionArguments(type));
    }

    /**
     * Sets the current request headers
     *
     * @param requestHeaders
     *
     * @returns {RequestExecutor}
     */
    public setRequestHeaders(requestHeaders?: object, skipGuestToken = false): RequestExecutor {
        const skipGuestTokenHeader = skipGuestToken ? { 'skip-intercept': 'true' } : {};
        this._requestHeaders = new HttpHeaders({
            ...requestHeaders,
            ...skipGuestTokenHeader,
            ...this._defaultRequestHeaders
        });

        return this;
    }

    /**
     * Sets the current request body
     *
     * @param requestBody
     *
     * @returns {RequestExecutor}
     */
    public setRequestBody(requestBody?: object): RequestExecutor {
        this._requestBody = requestBody;

        return this;
    }

    /**
     * Sets the current request parameters
     *
     * @param requestParams
     *
     * @returns {RequestExecutor}
     */
    public setRequestParams(requestParams?: any): RequestExecutor {
        this._requestParams = new HttpParams({
            fromObject: requestParams
        });

        return this;
    }

    /**
     * Generates the HTTP client invocation arguments
     *
     * @param type
     * @private
     * @returns {Array<any>}
     */
    private _generateFunctionArguments(type: ('get'|'post')): Array<any> {
        const functionArguments: Array<any> = [
            this._requestUrl
        ];

        if (type === Constants.REQUEST_TYPE_POST) {
            functionArguments.push(this._requestBody);
        }

        functionArguments.push({
            'headers': this._requestHeaders,
            'params': this._requestParams
        });

        return functionArguments;
    }
}
